import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Sparkles, Send, ArrowLeft, Copy, MoreVertical, HeartOff, Ban, Flag } from "lucide-react";
import { toast } from "sonner";

const REPORT_REASONS = [
  "Harassment or bullying",
  "Inappropriate content",
  "Spam or scam",
  "Impersonation",
  "Something else",
];

export default function Messages() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [messages, setMessages] = useState([]);
  const [threadMissing, setThreadMissing] = useState(false);
  const [text, setText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const [unmatchOpen, setUnmatchOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const bottomRef = useRef(null);

  const activeMatch = matches.find((m) => m.match_id === matchId);

  const refreshMatches = useCallback(
    () => api.get("/matches").then(({ data }) => setMatches(data)),
    []
  );

  useEffect(() => { refreshMatches(); }, [refreshMatches]);

  useEffect(() => {
    if (!matchId) { setThreadMissing(false); return; }
    setThreadMissing(false);
    api.get(`/messages/${matchId}`)
      .then(({ data }) => setMessages(data))
      .catch((err) => {
        if (err.response?.status === 404) setThreadMissing(true);
      });
  }, [matchId]);

  // Live Replies — poll for new messages while the chat is open and the tab is visible.
  useEffect(() => {
    if (!matchId || threadMissing) return;
    let cancelled = false;

    const tick = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const { data } = await api.get(`/messages/${matchId}`);
        if (cancelled) return;
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const additions = data.filter((m) => !seen.has(m.id));
          return additions.length ? [...prev, ...additions] : prev;
        });
      } catch (err) {
        if (err.response?.status === 404 && !cancelled) setThreadMissing(true);
      }
    };

    const id = setInterval(tick, 3000);
    // Also refresh the sidebar preview periodically (slower).
    const sidebarId = setInterval(() => { if (!document.hidden) refreshMatches(); }, 8000);
    return () => { cancelled = true; clearInterval(id); clearInterval(sidebarId); };
  }, [matchId, threadMissing, refreshMatches]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (customText) => {
    const payload = (customText ?? text).trim();
    if (!payload || !matchId) return;
    try {
      const { data } = await api.post("/messages", { match_id: matchId, text: payload });
      // Merge by id so the interval poll doesn't create duplicates.
      setMessages((m) => {
        const seen = new Set(m.map((x) => x.id));
        const toAdd = [];
        if (data.message && data.auto_reply) {
          if (!seen.has(data.message.id)) toAdd.push(data.message);
          if (!seen.has(data.auto_reply.id)) toAdd.push(data.auto_reply);
        } else if (data.message) {
          if (!seen.has(data.message.id)) toAdd.push(data.message);
        } else if (data?.id && !seen.has(data.id)) {
          toAdd.push(data);
        }
        return toAdd.length ? [...m, ...toAdd] : m;
      });
      setText("");
      setSuggestions([]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send. Try again.");
    }
  };

  const doUnmatch = async () => {
    try {
      await api.delete(`/matches/${matchId}`);
      toast.success("Unmatched");
      setUnmatchOpen(false);
      await refreshMatches();
      navigate("/messages");
    } catch {
      toast.error("Couldn't unmatch. Try again.");
    }
  };

  const doBlock = async () => {
    if (!activeMatch) return;
    try {
      await api.post(`/users/${activeMatch.user.user_id}/block`);
      toast.success(`Blocked ${activeMatch.user.name.replace(/^\[TEST\]\s*/, "").split(" ")[0]}`);
      setBlockOpen(false);
      await refreshMatches();
      navigate("/messages");
    } catch {
      toast.error("Couldn't block. Try again.");
    }
  };

  const doReport = async () => {
    if (!activeMatch || !reportReason) return;
    try {
      await api.post(`/users/${activeMatch.user.user_id}/report`, {
        reason: reportReason,
        details: reportDetails || null,
      });
      toast.success("Thanks — our team will review this.");
      setReportOpen(false);
      setReportReason("");
      setReportDetails("");
    } catch {
      toast.error("Couldn't send report. Try again.");
    }
  };

  const generateIcebreakers = async () => {
    if (!activeMatch) return;
    setGenLoading(true);
    try {
      const { data } = await api.post("/ai/icebreaker", { target_user_id: activeMatch.user.user_id });
      setSuggestions(data.suggestions);
    } catch {
      toast.error("AI is resting. Try again shortly.");
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Sidebar */}
        <aside className={`${matchId ? "hidden md:block" : ""} md:col-span-1`}>
          <h2 className="font-serif text-2xl font-semibold mb-4 text-[#2C2623]">Chats</h2>
          <div className="space-y-2">
            {matches.length === 0 && <p className="text-sm text-[#6E655F]">No matches yet.</p>}
            {matches.map((m) => (
              <Link key={m.match_id} to={`/messages/${m.match_id}`}
                data-testid={`message-list-item-${m.match_id}`}
                className={`flex items-center gap-3 p-3 rounded-2xl transition-colors ${
                  m.match_id === matchId
                    ? "bg-[#FFF0EC] border border-[#F5C9BC]"
                    : "hover:bg-[#F9F5EE]"
                }`}>
                <Avatar className="w-11 h-11">
                  <AvatarImage src={m.user.picture} />
                  <AvatarFallback className="bg-[#D95D39] text-white">{m.user.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="font-medium text-[#2C2623] truncate">{m.user.name}</div>
                  <div className="text-xs text-[#6E655F] truncate">{m.last_message?.text || "Say hello ✨"}</div>
                </div>
              </Link>
            ))}
          </div>
        </aside>

        {/* Chat panel */}
        <section className={`${matchId ? "" : "hidden md:flex"} md:col-span-2 flex flex-col`}>
          {threadMissing ? (
            <Card className="p-12 text-center rounded-3xl border-[#E8DEC8] bg-white" data-testid="chat-thread-missing">
              <p className="text-[#2C2623] font-serif text-xl mb-2">This conversation is no longer available</p>
              <p className="text-sm text-[#6E655F] mb-4">You may have unmatched or blocked this person. It's gone from both sides.</p>
              <Button onClick={() => navigate("/messages")} className="rounded-full bg-[#D95D39] hover:bg-[#C24C29]">
                Back to Messages
              </Button>
            </Card>
          ) : !activeMatch ? (
            <Card className="p-12 text-center rounded-3xl border-[#E8DEC8] bg-white">
              <p className="text-[#6E655F]">Pick a match to start chatting.</p>
            </Card>
          ) : (
            <Card className="flex flex-col rounded-3xl border-[#E8DEC8] bg-white h-[70vh] overflow-hidden">
              <div className="flex items-center gap-3 p-4 border-b border-[#E8DEC8]">
                <Button variant="ghost" size="icon" className="md:hidden" onClick={() => navigate("/messages")}>
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <Avatar className="w-10 h-10">
                  <AvatarImage src={activeMatch.user.picture} />
                  <AvatarFallback className="bg-[#D95D39] text-white">{activeMatch.user.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-base sm:text-lg font-semibold text-[#2C2623] truncate">
                    {activeMatch.user.name}
                  </div>
                  <div className="text-xs text-[#6E655F] flex items-center gap-1.5 flex-wrap">
                    {activeMatch.user.is_test_bot && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FEF3D6] text-[#B45309] font-bold uppercase tracking-wider"
                        data-testid="chat-test-bot-badge">Bot</span>
                    )}
                    <span className="truncate">{activeMatch.user.location || "Somewhere lovely"}</span>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="chat-menu-btn">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => setUnmatchOpen(true)} data-testid="chat-menu-unmatch">
                      <HeartOff className="w-4 h-4 mr-2" /> Unmatch
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setBlockOpen(true)} data-testid="chat-menu-block">
                      <Ban className="w-4 h-4 mr-2" /> Block
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setReportOpen(true)}
                      data-testid="chat-menu-report" className="text-destructive focus:text-destructive">
                      <Flag className="w-4 h-4 mr-2" /> Report
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-[#FFFDF9]">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div data-testid={`chat-message-${m.id}`}
                        className={`max-w-[75%] px-4 py-2.5 rounded-2xl leading-relaxed ${
                          mine
                            ? "bg-[#D95D39] text-white rounded-br-sm"
                            : "bg-white border border-[#E8DEC8] text-[#2C2623] rounded-bl-sm"
                        }`}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && (
                  <div className="text-center text-sm text-[#6E655F] pt-8">
                    You matched! Say something kind.
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {suggestions.length > 0 && (
                <div className="border-t border-[#E8DEC8] bg-[#FFF9F3] p-3 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-[#B45309] font-semibold flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> AI Icebreakers
                  </div>
                  {suggestions.map((s, i) => (
                    <button key={`ib-${s.slice(0, 32)}-${i}`} data-testid={`icebreaker-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="w-full text-left text-sm bg-white border border-[#E8DEC8] rounded-xl p-3 hover:bg-[#FFF0EC] flex items-start justify-between gap-3">
                      <span className="text-[#2C2623]">{s}</span>
                      <Copy className="w-3.5 h-3.5 text-[#6E655F] flex-shrink-0 mt-0.5" />
                    </button>
                  ))}
                </div>
              )}

              <div className="p-3 border-t border-[#E8DEC8] flex items-center gap-2">
                <Button onClick={generateIcebreakers} disabled={genLoading}
                  data-testid="ai-icebreaker-generate-btn"
                  variant="outline"
                  className="rounded-full border-[#E8DEC8] hover:bg-[#FFF0EC] text-[#B45309] gap-1.5">
                  <Sparkles className="w-4 h-4" />
                  {genLoading ? "…" : "AI Spark"}
                </Button>
                <Input value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  data-testid="chat-input-field"
                  placeholder="Say something warm…"
                  className="flex-1 rounded-full h-11 border-[#E8DEC8]" />
                <Button onClick={() => send()} data-testid="chat-send-button"
                  className="rounded-full h-11 w-11 bg-[#D95D39] hover:bg-[#C24C29] p-0">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          )}
        </section>
      </main>

      {/* Unmatch confirm */}
      <AlertDialog open={unmatchOpen} onOpenChange={setUnmatchOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">Unmatch this person?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll both stop seeing each other in Matches. Messages will be removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unmatch-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doUnmatch} data-testid="unmatch-confirm-btn"
              className="bg-[#D95D39] hover:bg-[#C24C29]">Yes, unmatch</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Block confirm */}
      <AlertDialog open={blockOpen} onOpenChange={setBlockOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">Block this person?</AlertDialogTitle>
            <AlertDialogDescription>
              They won't appear in Discover for you, and neither of you can message the other. Your existing match and messages will also be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="block-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doBlock} data-testid="block-confirm-btn"
              className="bg-[#2A524A] hover:bg-[#1F3E37]">Yes, block</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Report this person</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#6E655F]">
              Reports are private. Our team reviews every one to keep HerpMates safe.
            </p>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Reason</label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger data-testid="report-reason-select"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                <SelectContent>
                  {REPORT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Anything else? <span className="text-[#6E655F] text-xs">(optional)</span></label>
              <Textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)}
                data-testid="report-details-input" rows={3} placeholder="Add context if you want to." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)} data-testid="report-cancel-btn">Cancel</Button>
            <Button onClick={doReport} disabled={!reportReason} data-testid="report-submit-btn"
              className="bg-[#D95D39] hover:bg-[#C24C29]">Send report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
